import {
    BAA_SESSION_TIMEOUT_MINUTES,
    BAA_WARNING_BEFORE_TIMEOUT_MINUTES,
} from "@sp-crm/core";

const STORAGE_KEY = "sp_lastActivity";
const THROTTLE_MS = 30 * 1000;
const WARNING_BEFORE_TIMEOUT_MS = BAA_WARNING_BEFORE_TIMEOUT_MINUTES * 60 * 1000;

let lastActivityTimestamp = Date.now();
let lastStorageUpdate = 0;
let activityPaused = false;

const getIdleTimeMs = (): number => {
    return Date.now() - lastActivityTimestamp;
};

const getTimeUntilWarningMs = (): number => {
    const timeoutMs = BAA_SESSION_TIMEOUT_MINUTES * 60 * 1000;
    const warningAtMs = timeoutMs - WARNING_BEFORE_TIMEOUT_MS;
    return warningAtMs - getIdleTimeMs();
};

export const getTimeUntilLogoutMs = (): number => {
    const timeoutMs = BAA_SESSION_TIMEOUT_MINUTES * 60 * 1000;
    return timeoutMs - getIdleTimeMs();
};

export const shouldShowWarning = (): boolean => {
    return getTimeUntilWarningMs() <= 0 && getTimeUntilLogoutMs() > 0;
};

export const shouldLogout = (): boolean => {
    return getTimeUntilLogoutMs() <= 0;
};

const updateActivity = (): void => {
    if (activityPaused) {
        return;
    }

    const now = Date.now();

    if (now - lastStorageUpdate < THROTTLE_MS) {
        return;
    }

    lastActivityTimestamp = now;
    lastStorageUpdate = now;

    try {
        localStorage.setItem(STORAGE_KEY, String(now));
    } catch {
        // localStorage might be unavailable
    }
};

const handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== STORAGE_KEY || !event.newValue) {
        return;
    }

    const newTimestamp = parseInt(event.newValue, 10);
    if (!isNaN(newTimestamp) && newTimestamp > lastActivityTimestamp) {
        lastActivityTimestamp = newTimestamp;
    }
};

export const resetActivity = (): void => {
    lastActivityTimestamp = Date.now();
    lastStorageUpdate = Date.now();
    try {
        localStorage.setItem(STORAGE_KEY, String(lastActivityTimestamp));
    } catch {
        // localStorage might be unavailable
    }
};

export const pauseActivityTracking = (): void => {
    activityPaused = true;
};

export const resumeActivityTracking = (): void => {
    activityPaused = false;
};

let isInitialized = false;

export const initializeIdleTimer = (): (() => void) => {
    if (isInitialized) {
        return () => {};
    }

    isInitialized = true;
    resetActivity();

    const events = ["keydown", "click", "scroll", "touchstart"];
    events.forEach(event => {
        document.addEventListener(event, updateActivity, { passive: true });
    });

    window.addEventListener("storage", handleStorageEvent);

    return () => {
        events.forEach(event => {
            document.removeEventListener(event, updateActivity);
        });
        window.removeEventListener("storage", handleStorageEvent);
        isInitialized = false;
    };
};
